import type { IBlotterPreferenceState } from 'components/shared/Blotter/Grid/blotter.model';
import type { IBlotterItem } from 'models/IBlotterItem';
import type { IBlotterColumnDefinition } from 'state/blotters/common/blotter.model';

import type { ISecondaryMarketTradeBlotterFilters } from './secondaryMarketTradeBlotter.model';

export type SecondaryMarketTradeBlotterAction =
  | SecondaryMarketTradeBlotterFiltersLoaded
  | SecondaryMarketTradeBlotterColumnDefinitionReceived
  | SecondaryMarketTradeBlotterPageChanged
  | SecondaryMarketTradeBlotterItemsPerPageChanged
  | SecondaryMarketTradeBlotterFilterChanged
  | SecondaryMarketTradeBlotterAdvancedFiltersToggled
  | SecondaryMarketTradeBlotterItemsReceived
  | SecondaryMarketTradeBlotterItemsRequested
  | SecondaryMarketTradeBlotterItemsReloaded
  | SecondaryMarketTradeBlotterColumnStateChanged
  | SecondaryMarketTradeBlotterPreferenceStatePersisted
  | SecondaryMarketTradeBlotterPreferenceStateLoaded
  | SecondaryMarketTradeBlotterPreferenceStateLoadingFailed
  | SecondaryMarketTradeBlotterPreferenceStateGet;

//////////////////////////////////////////////////
export interface SecondaryMarketTradeBlotterFiltersLoaded {
  type: 'SECONDARY_MARKET_TRADE_BLOTTER_FILTERS_LOADED';
  filters: ISecondaryMarketTradeBlotterFilters;
}
export function secondaryMarketTradeBlotterFiltersLoaded(
  filters: ISecondaryMarketTradeBlotterFilters,
): SecondaryMarketTradeBlotterFiltersLoaded {
  return {
    type: 'SECONDARY_MARKET_TRADE_BLOTTER_FILTERS_LOADED',
    filters,
  };
}

//////////////////////////////////////////////////
export interface SecondaryMarketTradeBlotterColumnDefinitionReceived {
  type: 'SECONDARY_MARKET_TRADE_BLOTTER_COLUMN_DEFINITION_RECEIVED';
  columnDefinition: IBlotterColumnDefinition<IBlotterItem>[];
}
export function secondaryMarketTradeBlotterColumnDefinitionReceived(
  columnDefinition: IBlotterColumnDefinition<IBlotterItem>[],
): SecondaryMarketTradeBlotterColumnDefinitionReceived {
  return {
    type: 'SECONDARY_MARKET_TRADE_BLOTTER_COLUMN_DEFINITION_RECEIVED',
    columnDefinition,
  };
}

//////////////////////////////////////////////////
export interface SecondaryMarketTradeBlotterPageChanged {
  type: 'SECONDARY_MARKET_TRADE_BLOTTER_PAGE_CHANGED';
  page: number;
}
export function secondaryMarketTradeBlotterPageChanged(page: number): SecondaryMarketTradeBlotterPageChanged {
  return {
    type: 'SECONDARY_MARKET_TRADE_BLOTTER_PAGE_CHANGED',
    page,
  };
}

//////////////////////////////////////////////////
export interface SecondaryMarketTradeBlotterItemsPerPageChanged {
  type: 'SECONDARY_MARKET_TRADE_BLOTTER_ITEMS_PER_PAGE_CHANGED';
  itemsPerPage: number;
}
export function secondaryMarketTradeBlotterItemsPerPageChanged(
  itemsPerPage: number,
): SecondaryMarketTradeBlotterItemsPerPageChanged {
  return {
    type: 'SECONDARY_MARKET_TRADE_BLOTTER_ITEMS_PER_PAGE_CHANGED',
    itemsPerPage,
  };
}

//////////////////////////////////////////////////
type Filter = keyof ISecondaryMarketTradeBlotterFilters;

interface ISecondaryMarketTradeBlotterFilterChanged<F extends Filter> {
  type: 'SECONDARY_MARKET_TRADE_BLOTTER_FILTER_CHANGED';
  filter: F;
  value: ISecondaryMarketTradeBlotterFilters[F];
}
export type SecondaryMarketTradeBlotterFilterChanged = ISecondaryMarketTradeBlotterFilterChanged<Filter>;

export function secondaryMarketTradeBlotterFilterChanged<F extends Filter>(
  filter: F,
  value: ISecondaryMarketTradeBlotterFilters[F],
): SecondaryMarketTradeBlotterFilterChanged {
  return {
    type: 'SECONDARY_MARKET_TRADE_BLOTTER_FILTER_CHANGED',
    filter,
    value,
  };
}

//////////////////////////////////////////////////
export interface SecondaryMarketTradeBlotterAdvancedFiltersToggled {
  type: 'SECONDARY_MARKET_TRADE_BLOTTER_ADVANCED_FILTERS_TOGGLED';
}
export function secondaryMarketTradeBlotterAdvancedFiltersToggled(): SecondaryMarketTradeBlotterAdvancedFiltersToggled {
  return {
    type: 'SECONDARY_MARKET_TRADE_BLOTTER_ADVANCED_FILTERS_TOGGLED',
  };
}

//////////////////////////////////////////////////
export interface SecondaryMarketTradeBlotterItemsReceived {
  type: 'SECONDARY_MARKET_TRADE_BLOTTER_ITEMS_RECEIVED';
  items: readonly IBlotterItem[];
  totalItems: number;
  totalPages: number;
}
export function secondaryMarketTradeBlotterItemsReceived(
  items: readonly IBlotterItem[],
  totalItems: number,
  totalPages: number,
): SecondaryMarketTradeBlotterItemsReceived {
  return {
    type: 'SECONDARY_MARKET_TRADE_BLOTTER_ITEMS_RECEIVED',
    items,
    totalItems,
    totalPages,
  };
}

//////////////////////////////////////////////////
export interface SecondaryMarketTradeBlotterItemsRequested {
  type: 'SECONDARY_MARKET_TRADE_BLOTTER_ITEMS_REQUESTED';
}
export function secondaryMarketTradeBlotterItemsRequested(): SecondaryMarketTradeBlotterItemsRequested {
  return { type: 'SECONDARY_MARKET_TRADE_BLOTTER_ITEMS_REQUESTED' };
}

//////////////////////////////////////////////////
export interface SecondaryMarketTradeBlotterItemsReloaded {
  type: 'SECONDARY_MARKET_TRADE_BLOTTER_ITEMS_RELOADED';
}
export function secondaryMarketTradeBlotterItemsReloaded(): SecondaryMarketTradeBlotterItemsReloaded {
  return { type: 'SECONDARY_MARKET_TRADE_BLOTTER_ITEMS_RELOADED' };
}

//////////////////////////////////////////////////
export interface SecondaryMarketTradeBlotterColumnStateChanged {
  type: 'SECONDARY_MARKET_TRADE_BLOTTER_COLUMN_STATE_CHANGED';
  columnState: readonly any[];
}
export function secondaryMarketTradeBlotterColumnStateChanged(
  columnState: readonly any[],
): SecondaryMarketTradeBlotterColumnStateChanged {
  return { type: 'SECONDARY_MARKET_TRADE_BLOTTER_COLUMN_STATE_CHANGED', columnState };
}

//////////////////////////////////////////////////
export interface SecondaryMarketTradeBlotterPreferenceStatePersisted {
  type: 'SECONDARY_MARKET_TRADE_BLOTTER_PREFERENCE_STATE_PERSISTED';
  blotterPreferenceState: IBlotterPreferenceState;
}
export function secondaryMarketTradeBlotterPreferenceStatePersisted(
  blotterPreferenceState: IBlotterPreferenceState,
): SecondaryMarketTradeBlotterPreferenceStatePersisted {
  return { type: 'SECONDARY_MARKET_TRADE_BLOTTER_PREFERENCE_STATE_PERSISTED', blotterPreferenceState };
}

//////////////////////////////////////////////////
export interface SecondaryMarketTradeBlotterPreferenceStateLoaded {
  type: 'SECONDARY_MARKET_TRADE_BLOTTER_PREFERENCE_STATE_LOADED';
  blotterPreferenceState: IBlotterPreferenceState;
}
export function secondaryMarketTradeBlotterPreferenceStateLoaded(
  blotterPreferenceState: IBlotterPreferenceState,
): SecondaryMarketTradeBlotterPreferenceStateLoaded {
  return { type: 'SECONDARY_MARKET_TRADE_BLOTTER_PREFERENCE_STATE_LOADED', blotterPreferenceState };
}
//////////////////////////////////////////////////
export interface SecondaryMarketTradeBlotterPreferenceStateGet {
  type: 'SECONDARY_MARKET_TRADE_BLOTTER_PREFERENCE_STATE_GET';
}
export function secondaryMarketTradeBlotterPreferenceStateGet(): SecondaryMarketTradeBlotterPreferenceStateGet {
  return { type: 'SECONDARY_MARKET_TRADE_BLOTTER_PREFERENCE_STATE_GET' };
}

export function secondaryMarketTradeBlotterPreferenceStateLoadingFailed() {
  return {
    type: 'SECONDARY_MARKET_TRADE_BLOTTER_PREFERENCE_STATE_LOADING_FAILED' as const,
  };
}
export type SecondaryMarketTradeBlotterPreferenceStateLoadingFailed = ReturnType<
  typeof secondaryMarketTradeBlotterPreferenceStateLoadingFailed
>;
